import { handleError } from '@/helpers';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { FunctionComponent, useEffect } from 'react';
import { fetchSession, useReactQuerySession } from '../hooks/reactQuerySession';
import { forceRefreshQuery } from '@/constants';

let axiosInstance: AxiosInstance = axios.create({});
interface AxiosInterceptorProps {}

export const AxiosInterceptorComponent: FunctionComponent<AxiosInterceptorProps> = () => {
  const queryClient: QueryClient = useQueryClient();
  const handleErrorResponse = async (error: AxiosError) => {
    if (error.response) {
      if (error.response.status == 401) {
        // console.log(error);
        let fetchResponse = await fetchSession(forceRefreshQuery + '=true');
        console.log(fetchResponse, 'fetchResponse');
        if (fetchResponse?.accessToken) {
          queryClient.setQueriesData(['session'], () => {
            return fetchResponse;
          });
          axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + fetchResponse?.accessToken;
          //this typecast needed to be donne because of https://github.com/axios/axios/issues/5573
          return axiosInstance(error.config as AxiosRequestConfig);
        }
      } else {
        handleError(error.response.status);
      }
      return Promise.reject(error.response);
    } else {
      return Promise.reject(error);
    }
  };

  const getRequestInterceptor = (axiosInstance: AxiosInstance) => {
    return axiosInstance.interceptors.request.use(
      (request): InternalAxiosRequestConfig => {
        if (request.headers) {
          let common = request.headers.common;
          if (session?.accessToken) {
            if (!common?.Authorization) {
              request.headers.Authorization = 'Bearer ' + session?.accessToken;
            } else {
              request.headers.Authorization = common.Authorization;
            }
          }
          // attach some more headers here
        }
        return request;
      },
      async (error: AxiosError): Promise<AxiosError | AxiosResponse> => {
        return await handleErrorResponse(error);
      },
    );
  };
  const getResponseInterceptor = (axiosInstance: AxiosInstance) => {
    return axiosInstance.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => {
        return response;
      },
      async (error: AxiosError): Promise<AxiosError | AxiosResponse> => {
        return await handleErrorResponse(error);
      },
    );
  };
  const initInterceptor = (axiosInstance: AxiosInstance) => {
    let requestInterceptor = getRequestInterceptor(axiosInstance);
    let responseInterceptor = getResponseInterceptor(axiosInstance);
    return { requestInterceptor, responseInterceptor };
  };
  const { session } = useReactQuerySession();
  useEffect(() => {
    let { requestInterceptor, responseInterceptor } = initInterceptor(axiosInstance);
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [session?.accessToken]);

  return null;
};

export default axiosInstance;
